import { GridRowClassNameParams } from '@mui/x-data-grid-pro';
import type { Dayjs } from 'dayjs';
import { has } from 'lodash-es';
import { DateRangeOption, MarketsGroupedByMarketType } from '../@types';
import {
  Bet,
  BettingSlipFrom,
  BettingSlipWithMonitoringInfo,
  CustomerWalletTransaction,
  EventBase,
  EventPartScore,
  EventWithResults,
  Market,
  MarketType,
  Outcome,
  SpecialValue,
} from '../@types/api';
import { LiveIcon, MixIcon, PreMatchIcon } from '../components/icons';
import { SpecialValueFormInput } from '../hooks/useTicketAuthorization';
import { dayjs } from '../config/dayjs';
import { BETTING_TYPES } from '../constants';
import { floorToFixed } from '../utils';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500] as const;

export const getPageSizeOptions = (rowCount: number): number[] => {
  const optionIndex = PAGE_SIZE_OPTIONS.findIndex((size) => size >= rowCount);

  if (optionIndex === -1) {
    return [...PAGE_SIZE_OPTIONS];
  }

  return PAGE_SIZE_OPTIONS.slice(0, optionIndex + 1);
};

export const splitCamelcaseString = (string: string): string => {
  const splitWords = string.split(/(?=[A-Z])/);

  const capitalizeWords = splitWords.map((word) => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return capitalizeWords.join(' ');
};

export const setValueAsNumberOrNull = (value: string) => {
  return !value ? null : Number(value);
};

export const isNumeric = (value: string) => {
  return !isNaN(Number(value));
};

type SpecialValueWithFormattedValue = Omit<SpecialValue, 'value'> & { value: string[] | number | null };
type Obj = EventBase | EventWithResults | Market | Outcome | SpecialValueWithFormattedValue;

export const getLatestValue = (obj: Obj, key: string) =>
  // @ts-expect-error
  has(obj, `latestChanges.${key}`) ? obj.latestChanges[key] : obj[key];

export const getOutcomeCellColor = (outcome: Outcome) => {
  const latestChanges = outcome.latestChanges;

  if (!outcome?.odds || !latestChanges?.odds) return 'black';

  return latestChanges?.odds && Number(latestChanges.odds) < Number(outcome?.odds) ? 'red' : 'green';
};

export const sortMarketsByPositionAndName = (markets: (Market | MarketsGroupedByMarketType)[]) => {
  const marketsCopy = [...markets];

  return marketsCopy.sort((a, b) => {
    if (a.position && b.position) {
      return a.position - b.position;
    }
    if (a.position) {
      return -1;
    }
    if (b.position) {
      return 1;
    }
    return a.name?.localeCompare(b?.name);
  });
};

export const getBettingSlipFromIcon = (eventFrom: BettingSlipFrom) => {
  switch (eventFrom) {
    case 'Pre-match':
      return PreMatchIcon;
    case 'In-play':
      return LiveIcon;
    case 'Mix':
      return MixIcon;
    default:
      return PreMatchIcon;
  }
};

export const isDev = () => import.meta.env.DEV;

export const initializeTicketAuthorizationFormData = (
  ticketDetails: BettingSlipWithMonitoringInfo | undefined,
  isOMFeed: boolean
) => {
  if (!ticketDetails) {
    return {
      totalStakeAmount: '',
      specialValues: {},
      odds: {},
    };
  }

  const specialValues = ticketDetails.bets.reduce((acc: Record<string, SpecialValueFormInput[]>, bet: Bet) => {
    const specialValue = isOMFeed
      ? (bet.specialValues?.[0]?.value ?? null)
      : (bet.outcome.properties?.specialValue ?? null);

    if (specialValue !== null) {
      return {
        ...acc,
        [bet.id]: [
          {
            id: bet.outcome.id,
            marketId: bet.outcome.marketId,
            name: specialValue.toString(),
            odds: bet.odds.toString(),
          },
        ],
      };
    }
    return acc;
  }, {});

  const odds = ticketDetails.bets.reduce((acc: Record<string, string>, bet: Bet) => {
    const hasSpecialValue = isOMFeed ? bet.specialValues.length > 0 : !!bet.outcome.properties?.specialValue;

    if (hasSpecialValue) {
      return acc;
    }

    return {
      ...acc,
      [bet.outcome.id]: bet.odds.toString(),
    };
  }, {});

  return {
    totalStakeAmount: ticketDetails.totalStakeAmount.toString(),
    specialValues,
    odds,
  };
};

export const isUUID = (value: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  return uuidRegex.test(value);
};

export const generateDateRangeTimestamps = (dateRangeOption: DateRangeOption) => {
  const now = dayjs();

  let fromTimestamp: Dayjs;
  let toTimestamp: Dayjs;

  switch (dateRangeOption) {
    case 'today':
      fromTimestamp = now.startOf('day');
      toTimestamp = now.endOf('day');
      break;
    case 'yesterday':
      fromTimestamp = now.subtract(1, 'day').startOf('day');
      toTimestamp = now.subtract(1, 'day').endOf('day');
      break;
    case 'this_week':
      fromTimestamp = now.startOf('week');
      toTimestamp = now.endOf('week');
      break;
    case 'last_week':
      fromTimestamp = now.subtract(1, 'week').startOf('week');
      toTimestamp = now.subtract(1, 'week').endOf('week');
      break;
    case 'last_7_days':
      fromTimestamp = now.subtract(7, 'day').startOf('day');
      toTimestamp = now.endOf('day');
      break;
    case 'last_30_days':
      fromTimestamp = now.subtract(30, 'day').startOf('day');
      toTimestamp = now.endOf('day');
      break;
    case 'last_3_months':
      fromTimestamp = now.subtract(3, 'month').startOf('day');
      toTimestamp = now.endOf('day');
      break;
    case 'last_6_months':
      fromTimestamp = now.subtract(6, 'month').startOf('day');
      toTimestamp = now.endOf('day');
      break;
    case 'last_12_months':
      fromTimestamp = now.subtract(12, 'month').startOf('day');
      toTimestamp = now.endOf('day');
      break;
    case 'this_month':
      fromTimestamp = now.startOf('month');
      toTimestamp = now.endOf('month');
      break;
    case 'last_month':
      fromTimestamp = now.subtract(1, 'month').startOf('month');
      toTimestamp = now.subtract(1, 'month').endOf('month');
      break;
    default:
      fromTimestamp = now.startOf('day');
      toTimestamp = now.endOf('day');
  }

  if (toTimestamp.isAfter(now)) {
    toTimestamp = now.endOf('day');
  }

  return { fromTimestamp, toTimestamp };
};

export const walletValueGetter = (
  walletKey: 'mainWallet' | 'bonusWallet',
  field: 'amountIn' | 'amountOut' | 'balance'
) => {
  return (_value: unknown, row: CustomerWalletTransaction) => {
    const wallet = row[walletKey];

    return wallet && wallet[field] ? floorToFixed(wallet[field], 2) : '--';
  };
};

export const getTotalsRowClass = (params: GridRowClassNameParams) => {
  const rowId = params.id ? params.id : params.row.userid;
  if (rowId === 'totals' || rowId === 'Totals:') {
    return 'totals';
  }
  return '';
};

export const formatScore = (mainEventPartScore: EventPartScore | null) => {
  const { valueAway, valueHome } = mainEventPartScore || {};

  if ((valueHome || valueHome === 0) && (valueAway || valueAway === 0)) {
    return `${valueHome} - ${valueAway}`;
  }
  return null;
};

export const getNameToDisplay = ({ shortName, name }: { name: string; shortName?: string }) => shortName ?? name;

export const getMarketTypeName = (marketType?: MarketType, showShortName = true) => {
  if (!marketType) return '';

  const marketName = showShortName ? getNameToDisplay(marketType) : marketType.name;

  // Only render event part name when displaying full market name
  const eventPart = !marketType.shortName && marketType.eventPartName ? `, ${marketType.eventPartName}` : '';

  return `${marketName}${eventPart}`;
};

export const getModelTypeLabel = (index: number) => {
  if (index === 0) return 'First';
  if (index === 1) return 'Second';
  if (index === 2) return 'Third';
  return '';
};

export const getTicketBettingType = (ticketDetails: BettingSlipWithMonitoringInfo | undefined) => {
  const systemBettingType =
    ticketDetails?.requiredHitCount && ticketDetails.bettingSlipType !== 'Single'
      ? ` / ${BETTING_TYPES[ticketDetails.requiredHitCount - 1]}`
      : '';

  return `${ticketDetails?.bettingSlipType}${systemBettingType}`;
};

export const getNameOptionLabel = <O extends { name: string; id: string }>(options: O[], value: string) => {
  const option = options.find((option) => option?.id === value);
  return option?.name || '';
};

export const appendTotalsRow = <T, U>(items: T[] | undefined, totalsRow: U | undefined) => {
  const data = items || [];

  if (totalsRow) {
    return [...data, totalsRow];
  }

  return data;
};
